// src/components/StripeCardForm.js
//
// The embedded card-entry form itself — must be a child of <Elements>
// (react-stripe-js requires useStripe()/useElements() to run inside that
// provider). Kept separate from the checkout page so the page doesn't have
// to re-render the whole Elements tree on every keystroke.
import { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

function StripeCardForm({ onSuccess, onFailure }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setMessage('');

    // redirect: 'if_required' is what keeps the customer on this page for
    // the common case (a normal card payment). Stripe only navigates away
    // for payment methods that fundamentally require it (e.g. certain bank
    // redirects) — a standard card, including 3D Secure challenges, is
    // handled entirely in an in-page Stripe iframe/modal.
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setSubmitting(false);
      const friendly = error.message || 'Your card could not be processed. Please try again.';
      setMessage(friendly);
      onFailure?.(friendly);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Leave submitting=true — the parent takes over (confirming the
      // order server-side, then navigating to the success page).
      onSuccess?.(paymentIntent.id);
      return;
    }

    setSubmitting(false);
    const friendly = 'Payment did not complete. Please try again.';
    setMessage(friendly);
    onFailure?.(friendly);
  }

  return (
    <form onSubmit={handleSubmit} className="stripe-card-form">
      <PaymentElement />
      {message && <div className="stripe-card-form-error">{message}</div>}
      <button type="submit" className="stripe-card-submit-btn" disabled={!stripe || submitting}>
        {submitting ? 'Processing…' : 'Pay now'}
      </button>
    </form>
  );
}

export default StripeCardForm;
